import { useCompanyTimezoneStore } from "@/stores/companyTimezone";

export const getCompanyTimezone = () =>
  useCompanyTimezoneStore.getState().timeZone;

/** A calendar value for pickers/default periods, not an instant for storage. */
export function companyCalendarDate(instant = new Date()): Date {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: getCompanyTimezone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const value = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  return new Date(
    value("year"),
    value("month") - 1,
    value("day"),
    value("hour"),
    value("minute"),
    value("second"),
  );
}

function formatted(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  if (!value) return "-";
  // A date-only business value has no timezone. Preserve the chosen calendar day.
  const calendar =
    typeof value === "string" && /^\d{4}-\d{2}(?:-\d{2})?$/.test(value);
  const date =
    value instanceof Date
      ? value
      : new Date(calendar && value.length === 7 ? `${value}-01` : value);
  if (!Number.isFinite(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: calendar ? "UTC" : getCompanyTimezone(),
  }).format(date);
}
export function formatCompanyDate(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatted(
    value,
    options ?? { year: "numeric", month: "short", day: "numeric" },
  );
}
export function formatCompanyDateTime(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatted(
    value,
    options ?? {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  );
}
export function formatCompanyTime(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatted(value, options ?? { hour: "2-digit", minute: "2-digit" });
}

/** Audit instants include the organisation's offset at the time of the event. */
export function formatAuditDateTime(
  value: string | Date | null | undefined,
): string {
  return formatCompanyDateTime(value, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "longOffset",
  });
}

export function formatCalendarDate(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return "-";
  const date =
    value instanceof Date
      ? Number.isFinite(value.getTime())
        ? value.toISOString()
        : ""
      : value;
  return formatCompanyDate(date.slice(0, 10), options);
}
